import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, createBlock as _createBlock, createTextVNode as _createTextVNode, Fragment as _Fragment, renderList as _renderList, vModelCheckbox as _vModelCheckbox, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "mb-3 font-semibold" }
const _hoisted_2 = {
  key: 0,
  class: "text-sm"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 2,
  class: "my-2 text-sm text-indigo-700",
  role: "status"
}
const _hoisted_6 = { class: "my-3 text-sm font-semibold" }
const _hoisted_7 = {
  key: 3,
  class: "block text-sm"
}
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 4,
  class: "mt-2 text-sm text-gray-600"
}
const _hoisted_10 = { class: "mt-3 flex flex-wrap gap-3" }
const _hoisted_11 = ["value", "max", "disabled", "onInput"]
const _hoisted_12 = {
  key: 5,
  class: "mt-3 text-sm text-indigo-700",
  role: "status"
}
const _hoisted_13 = {
  key: 2,
  role: "alert",
  class: "mt-3 rounded bg-red-50 p-2 text-sm text-red-700"
}
const _hoisted_14 = { class: "w-full space-y-3" }
const _hoisted_15 = { class: "flex items-start gap-2 text-sm" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "flex justify-end gap-2" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]

import { computed, onUnmounted, ref, watch } from 'vue';
import ModalComp from '@/components/ui/ModalComp.vue';
import ReplacementProductPicker from './ReplacementProductPicker.vue';
import ReplacementComparison from './ReplacementComparison.vue';
import { changeReplacementProduct, fetchReplacementProducts, previewReplacement, removeReplacementCandidate, replacementLineProducts, updateReplacementQuantity } from '@/api/remplacements';
import type { ReplacementAppliedProduct, ReplacementLine, ReplacementProduct, ReplacementProposal, ReplacementSelection } from '@/api/remplacements';

type Action = 'quantity' | 'remove' | 'product';

export default /*@__PURE__*/_defineComponent({
  __name: 'EditReplacementCandidateModal',
  emits: ["saved"],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit;
const visible = ref(false), busy = ref(false), loading = ref(false), quoting = ref(false), confirmed = ref(false);
const action = ref<Action>('quantity');
const proposal = ref<ReplacementProposal | null>(null);
const line = ref<ReplacementLine | null>(null);
const original = ref<ReplacementAppliedProduct | null>(null);
const selection = ref<ReplacementSelection>({ id_ligne_origine: 0, id_produit_remplacement: 0, quantites: Array(12).fill(0), quantite_pack: 1 });
const products = ref<ReplacementProduct[]>([]), choices = ref<ReplacementProduct[]>([]);
const search = ref(''), error = ref(''), quoteHash = ref('');
const preview = ref<ReplacementLine[]>([]);
let revision = 0, quoteRevision = 0, searchRevision = 0;
let timer: ReturnType<typeof window.setTimeout> | null = null;
const title = computed(() => action.value === 'quantity' ? 'Modifier la quantité recommandée' : action.value === 'remove' ? 'Annuler le candidat' : 'Changer le produit recommandé');
const selected = computed(() => products.value.find(product => product.id_produit === selection.value.id_produit_remplacement));
const sizes = computed(() => selected.value?.tailles.flatMap((label, index) => selected.value?.vendu_par === 'tailles'
  ? label ? [{ label, index }] : [] : selected.value?.pack[index] ? [{ label: label || 'Assortiment', index }] : []) || []);
const valid = computed(() => {
  const product = selected.value, value = selection.value;
  if (!product || value.quantites.length !== 12 || value.quantites.some(q => !Number.isSafeInteger(q) || q < 0 || q > 10000)) return false;
  const count = value.quantites.reduce((sum, q) => sum + q, 0);
  return count > 0 && count <= 10000 && Number.isSafeInteger(value.quantite_pack) && value.quantite_pack >= 1 && value.quantite_pack <= 10000 &&
    (product.vendu_par === 'tailles' ? count >= product.qte_mini && value.quantites.every((q, i) => !q || Boolean(product.tailles[i])) : value.quantites.every((q, i) => q <= product.pack[i] * value.quantite_pack)) &&
    (action.value !== 'product' || value.id_produit_remplacement !== original.value?.id_produit);
});
function stopQuote() { if (timer != null) window.clearTimeout(timer); timer = null; quoteRevision++; quoting.value = false; }
function invalidate() {
  stopQuote(); quoteHash.value = ''; preview.value = []; confirmed.value = false;
  if (visible.value && action.value !== 'remove' && !loading.value && valid.value) {
    quoting.value = true;
    timer = window.setTimeout(() => { void quote(); }, 300);
  }
}
function updateSizeQuantity(index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.value === '' && !input.validity.badInput) input.value = '0';
  selection.value.quantites[index] = input.value === '' ? NaN : Number(input.value);
}
function updatePacks() { if (selected.value) selection.value.quantites = selected.value.pack.map(q => q * selection.value.quantite_pack); }
function choose(id: number) {
  if (id === selection.value.id_produit_remplacement) return;
  selection.value.id_produit_remplacement = id; selection.value.quantite_pack = 1;
  selection.value.quantites = Array(12).fill(0);
  if (selected.value?.vendu_par !== 'tailles') updatePacks();
}
async function searchProducts() {
  const owner = proposal.value, origin = line.value;
  if (!owner || !origin) return;
  const current = revision, request = ++searchRevision;
  loading.value = true; error.value = ''; stopQuote();
  try {
    const rows = await fetchReplacementProducts(owner.id_commande, search.value.trim(), origin.id_ligne_origine, true);
    if (current !== revision || request !== searchRevision || !visible.value) return;
    choices.value = rows.filter(product => product.id_produit !== original.value?.id_produit &&
      !replacementLineProducts(origin).some(candidate => candidate.id_produit !== original.value?.id_produit && candidate.id_produit === product.id_produit));
    const merged = new Map(products.value.map(product => [product.id_produit, product]));
    rows.forEach(product => merged.set(product.id_produit, product)); products.value = [...merged.values()];
  } catch (err) { if (current === revision) error.value = err instanceof Error ? err.message : 'Recherche impossible'; }
  finally { if (current === revision && request === searchRevision) { loading.value = false; invalidate(); } }
}
async function open(value: ReplacementProposal, origin: ReplacementLine, candidate: ReplacementAppliedProduct, operation: Action) {
  const current = ++revision; stopQuote();
  proposal.value = JSON.parse(JSON.stringify(value)); line.value = JSON.parse(JSON.stringify(origin)); original.value = JSON.parse(JSON.stringify(candidate));
  action.value = operation; error.value = ''; search.value = ''; quoteHash.value = ''; preview.value = []; confirmed.value = false;
  products.value = []; choices.value = [];
  selection.value = { id_ligne_origine: origin.id_ligne_origine, id_produit_remplacement: candidate.id_produit, quantites: [...candidate.quantites], quantite_pack: candidate.quantite_pack };
  visible.value = true; loading.value = operation !== 'remove';
  if (operation === 'remove') return;
  try {
    const rows = await fetchReplacementProducts(value.id_commande, candidate.reference, origin.id_ligne_origine, true);
    if (current !== revision || !visible.value) return;
    products.value = rows;
    if (!rows.some(product => product.id_produit === candidate.id_produit) && operation === 'quantity') error.value = 'Ce produit n’est plus disponible. Choisissez un autre candidat.';
  } catch (err) { if (current === revision) error.value = err instanceof Error ? err.message : 'Chargement impossible'; }
  finally { if (current === revision) { loading.value = false; invalidate(); } }
  if (current === revision && visible.value && operation === 'product') await searchProducts();
}
async function quote() {
  stopQuote();
  const owner = proposal.value, origin = line.value, previous = original.value;
  if (!valid.value || !owner || !origin || !previous || !visible.value) return;
  const request = quoteRevision, current = revision;
  quoting.value = true; error.value = '';
  const selections = replacementLineProducts(origin).map(candidate => candidate.id_produit === previous.id_produit
    ? { ...selection.value, quantites: [...selection.value.quantites] }
    : { id_ligne_origine: origin.id_ligne_origine, id_produit_remplacement: candidate.id_produit, quantites: [...candidate.quantites], quantite_pack: candidate.quantite_pack });
  try {
    const result = await previewReplacement({ id_commande: owner.id_commande, lignes: selections, stock_confirme: false }, true);
    if (current !== revision || request !== quoteRevision || !visible.value) return;
    quoteHash.value = result.quote_hash; preview.value = result.lignes;
  } catch (err) { if (current === revision && request === quoteRevision) error.value = err instanceof Error ? err.message : 'Calcul impossible'; }
  finally { if (current === revision && request === quoteRevision) quoting.value = false; }
}
async function save() {
  if (busy.value || loading.value || quoting.value || !confirmed.value || !proposal.value || !line.value || !original.value || (action.value !== 'remove' && (!valid.value || !quoteHash.value))) return;
  busy.value = true; error.value = '';
  const target = { id_jet: proposal.value.id_jet, id_ligne_origine: line.value.id_ligne_origine, id_produit: original.value.id_produit };
  const context = { id_commande: proposal.value.id_commande, version: proposal.value.version };
  const quantity = { ...context, quantites: [...selection.value.quantites], quantite_pack: selection.value.quantite_pack, stock_confirme: true, quote_hash: quoteHash.value };
  try {
    if (action.value === 'remove') await removeReplacementCandidate(target, context);
    else if (action.value === 'quantity') await updateReplacementQuantity(target, quantity);
    else await changeReplacementProduct(target, { ...quantity, id_produit_remplacement: selection.value.id_produit_remplacement });
    visible.value = false; emit('saved');
  } catch (err) { error.value = err instanceof Error ? err.message : 'Modification impossible'; confirmed.value = false; }
  finally { busy.value = false; }
}
watch(() => JSON.stringify(selection.value), invalidate, { flush: 'sync' });
watch(visible, value => { if (!value) { revision++; stopQuote(); loading.value = false; } });
onUnmounted(stopQuote);
__expose({ open });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalComp, {
    isVisible: visible.value,
    "onUpdate:isVisible": _cache[4] || (_cache[4] = ($event: any) => ((visible).value = $event)),
    title: title.value,
    size: "lg"
  }, {
    body: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(original.value?.reference), 1),
      (action.value === 'remove')
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, "Retirer ce candidat ? L’article reste en rupture."))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (action.value === 'product')
              ? (_openBlock(), _createElementBlock("form", {
                  key: 0,
                  class: "mb-3 flex gap-2",
                  onSubmit: _withModifiers(searchProducts, ["prevent"])
                }, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
                    disabled: busy.value,
                    placeholder: "Référence du nouveau produit",
                    class: "min-w-0 flex-1 rounded border p-2",
                    "aria-label": "Référence du nouveau produit"
                  }, null, 8, _hoisted_3), [
                    [_vModelText, search.value]
                  ]),
                  _createElementVNode("button", {
                    type: "submit",
                    disabled: busy.value || loading.value,
                    class: "rounded border px-3 py-2"
                  }, "Rechercher", 8, _hoisted_4)
                ], 32))
              : _createCommentVNode("", true),
            (action.value === 'product')
              ? (_openBlock(), _createBlock(ReplacementProductPicker, {
                  key: 1,
                  products: choices.value,
                  "selected-ids": [selection.value.id_produit_remplacement],
                  selections: [selection.value],
                  loading: loading.value,
                  disabled: busy.value,
                  onChoose: choose
                }, null, 8, ["products", "selected-ids", "selections", "loading", "disabled"]))
              : _createCommentVNode("", true),
            (loading.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_5, "Chargement des produits…"))
              : _createCommentVNode("", true),
            _createElementVNode("p", _hoisted_6, [
              _cache[5] || (_cache[5] = _createTextVNode("Quantité recommandée")),
              (selected.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" · " + _toDisplayString(selected.value.reference), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            (selected.value && selected.value.vendu_par !== 'tailles')
              ? (_openBlock(), _createElementBlock("label", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createTextVNode("Quantité de paquets ")),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selection.value.quantite_pack) = $event)),
                    type: "number",
                    min: "1",
                    max: "10000",
                    disabled: busy.value || loading.value,
                    class: "ml-2 w-24 rounded border p-2",
                    onInput: updatePacks
                  }, null, 40, _hoisted_8), [
                    [
                      _vModelText,
                      selection.value.quantite_pack,
                      void 0,
                      { number: true }
                    ]
                  ])
                ]))
              : _createCommentVNode("", true),
            (selected.value && selected.value.vendu_par !== 'tailles')
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, "0 = taille non souhaitée. Modifier les paquets réinitialise les quantités."))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sizes.value, (size) => {
                return (_openBlock(), _createElementBlock("label", {
                  key: size.index,
                  class: "text-sm"
                }, [
                  _createTextVNode(_toDisplayString(size.label) + " ", 1),
                  _createElementVNode("input", {
                    value: selection.value.quantites[size.index],
                    type: "number",
                    min: "0",
                    max: selected.value?.vendu_par === 'tailles' ? 10000 : Math.min(10000, (selected.value?.pack[size.index] || 0) * selection.value.quantite_pack),
                    disabled: busy.value || loading.value,
                    class: "mt-1 block w-20 rounded border p-2",
                    onInput: ($event: any) => (updateSizeQuantity(size.index, $event))
                  }, null, 40, _hoisted_11)
                ]))
              }), 128))
            ]),
            (quoting.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_12, "Calcul du devis…"))
              : _createCommentVNode("", true),
            (preview.value.length)
              ? (_openBlock(), _createBlock(ReplacementComparison, {
                  key: 6,
                  lines: preview.value,
                  class: "mt-3"
                }, null, 8, ["lines"]))
              : _createCommentVNode("", true)
          ], 64)),
      (error.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(error.value), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("label", _hoisted_15, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((confirmed).value = $event)),
            type: "checkbox",
            disabled: busy.value || loading.value,
            class: "mt-1"
          }, null, 8, _hoisted_16), [
            [_vModelCheckbox, confirmed.value]
          ]),
          _createElementVNode("span", null, _toDisplayString(action.value === 'remove' ? 'Je confirme le retrait.' : 'Je confirme la disponibilité et les écarts de prix à ma charge.'), 1)
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("button", {
            type: "button",
            disabled: busy.value,
            class: "rounded border px-3 py-2",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (visible.value = false))
          }, "Fermer", 8, _hoisted_18),
          _createElementVNode("button", {
            type: "button",
            disabled: busy.value || loading.value || quoting.value || !confirmed.value || (action.value !== 'remove' && (!valid.value || !quoteHash.value)),
            class: "rounded bg-indigo-600 px-3 py-2 text-white disabled:opacity-50",
            onClick: save
          }, _toDisplayString(busy.value ? 'Enregistrement…' : action.value === 'remove' ? 'Annuler ce candidat' : 'Enregistrer'), 9, _hoisted_19)
        ])
      ])
    ]),
    _: 1
  }, 8, ["isVisible", "title"]))
}
}

})